import React, {
  ElementRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import {
  RegularReservationSettingProps,
  ReservationTimeFrameProps,
} from 'types/med'
import { ReservationFrameUnitMin } from 'types/common'

import { TimeString } from 'config/format'
import { format, addMinutes, parse } from 'date-fns'
import { Box, Button, FormControlLabel, MenuItem, Select } from '@mui/material'
import { buildStartEndList } from 'ui/utils/AvailableDateTimes'
import { StartEnd, timeStringToNumber } from 'ui/utils/AvailableDateTimes'
import {
  ReservationFrameSettingCellReadOnly,
  RegularReservationFrameDayOfWeek,
  ReservationSettingOfTimeCell,
  ReservationFrameTimes,
  ReservationFrameCell,
  ReservationFrameColumn,
} from './ReservationFrameSettingCell'
import isEqualRegularReservationSettings from 'ui/utils/isEqualRegularReservationSettings'

// NOTE: 開始時刻指定なのでスクロールさせない
export const INITIAL_SCROLL_TOP = 0

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    overflowX: 'auto',
  },
  tableScrollXContainer: {
    minWidth: 783,
  },
  dayOfWeeks: {
    display: 'grid',
    gridTemplateColumns: '48px repeat(7, minmax(103px, 1fr))',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  scrollContainer: {
    maxHeight: 492,
    display: 'grid',
    gridTemplateColumns: '48px repeat(7, minmax(103px, 1fr))',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  applyDropdownContainer: {
    margin: 0,
  },
  applyDropdown: {
    height: '36px',
    boxSizing: 'border-box',
    width: '115px',
    marginLeft: theme.spacing(2),
    fontSize: theme.typography.fontSize,
  },
}))

const dayOfWeeks = [1, 2, 3, 4, 5, 6, 0]

export type DayOfWeekCheckStatus = {
  [startTime: string]: boolean
}

type CheckStatusesOfWeek = { [dayOfWeek: string]: DayOfWeekCheckStatus }

type ReservationSettingProps = {
  unit: number
  dayOfWeek: number
  reservationSetting?: RegularReservationSettingProps
  checkStatus: DayOfWeekCheckStatus
  onChange: (reservationSetting: RegularReservationSettingProps) => void
  onChangeCheckStatus: (status: DayOfWeekCheckStatus, dayOfWeek: number) => void
  startEnds: StartEnd[]
  readOnly?: boolean
}

const RegularReservationSettingColumn: React.FC<ReservationSettingProps> = ({
  dayOfWeek,
  reservationSetting,
  checkStatus,
  onChange,
  onChangeCheckStatus,
  startEnds,
  readOnly,
  unit,
}: ReservationSettingProps) => {
  const maxNumber = (20 * unit) / 30

  const onChangeRef = useRef(onChange)
  const onChangeCheckStatusRef = useRef(onChangeCheckStatus)

  // NOTE: 再描画が重いのでキャッシュする
  return useMemo(
    () => {
      const frameMap = new Map<string, ReservationTimeFrameProps>()
      reservationSetting?.reservation_frames.forEach((data) => {
        frameMap.set(data.start_time, data)
      })

      return (
        <ReservationFrameColumn>
          {startEnds.map((time) => {
            const frame = frameMap.get(time.start) || undefined
            return readOnly ? (
              <ReservationFrameSettingCellReadOnly
                key={time.start}
                number={frame?.number_of_people || 0}
              />
            ) : (
              <ReservationSettingOfTimeCell
                maxNumber={maxNumber}
                key={time.start}
                time={time}
                checked={checkStatus[time.start] ?? false}
                reservationTimeFrame={frame}
                onChangeCheckStatus={(status) =>
                  onChangeCheckStatusRef.current?.(
                    { ...checkStatus, ...status },
                    dayOfWeek
                  )
                }
                onChange={(timeFrame) => {
                  const reservation_frames =
                    reservationSetting?.reservation_frames.filter(
                      (f) => f.start_time !== timeFrame.start_time
                    ) ?? []

                  // NOTE: number_of_people == 0 は弾く
                  if (timeFrame.number_of_people > 0) {
                    reservation_frames.push(timeFrame)
                  }

                  onChangeRef.current?.({
                    day_of_week: dayOfWeek,
                    reservation_frames,
                  })
                }}
              />
            )
          })}
        </ReservationFrameColumn>
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reservationSetting, checkStatus, readOnly, dayOfWeek, startEnds]
  )
}

export const getTimeEnd = (timeStart: string, offset: number): string => {
  const start = parse(timeStart, 'HH:mm', new Date())
  const end = addMinutes(start, offset)
  return format(end, TimeString)
}

type PeriodicReservationTableProps = {
  /** 予約設定一覧 */
  reservationSettings: RegularReservationSettingProps[]

  /** 内容が更新された場合に発火 */
  onChange?: (reservationSettings: RegularReservationSettingProps[]) => void

  /** カレンダー開始時刻 */
  from: string

  /** カレンダー終了時刻 */
  to: string

  /** 参照のみのモードを指定 */
  readOnly?: boolean

  unit: ReservationFrameUnitMin
}
const RegularReservationFrameSetting: React.FC<
  PeriodicReservationTableProps
> = ({
  reservationSettings,
  onChange,
  from = '08:00',
  to = '20:00',
  readOnly,
  unit = 30,
}: PeriodicReservationTableProps) => {
  const { t } = useTranslation()

  // NOTE: Refを介して最新の値を参照させる
  const reservationSettingsRef = useRef(reservationSettings)
  reservationSettingsRef.current = reservationSettings
  const onChangeRef = useRef(onChange)

  const maxNumber = (20 * unit) / 30

  const { classes, cx } = useStyles()

  const [checkStatuses, setCheckStatuses] = useState<CheckStatusesOfWeek>(
    Object.fromEntries(dayOfWeeks.map((d) => [d, {}]))
  )

  const [applyDropdownSelection, setApplyDropdownSelection] =
    useState<number>(0)

  const [startEnds, setStartEnds] = useState<StartEnd[]>([])

  useEffect(() => {
    setStartEnds(
      buildStartEndList({
        from,
        to,
        unit,
      })
    )
  }, [from, to, unit])

  useEffect(() => {
    if (applyDropdownSelection > maxNumber) {
      setApplyDropdownSelection(maxNumber)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationSettings, maxNumber])

  // NOTE: ドロップダウンorチェックが変わった際に人数を適用
  useEffect(() => {
    const appliedReservationSettings: RegularReservationSettingProps[] =
      dayOfWeeks
        .map((day_of_week) => {
          const checkedTimes = Object.entries(checkStatuses[day_of_week])
            .filter(([, checked]) => checked)
            .map(([timeStart]) => timeStart)

          // チェックされていない既存のReservationTimeFrameProps
          const unCheckedExistingFrames: ReservationTimeFrameProps[] =
            reservationSettingsRef.current
              .find((s) => s.day_of_week === day_of_week)
              ?.reservation_frames.filter(
                (f) => !checkedTimes.includes(f.start_time)
              ) ?? []

          // チェックされた時間のReservationTimeFrameProps
          const checkedFrames: ReservationTimeFrameProps[] = checkedTimes.map(
            (start_time) => ({
              start_time: start_time,
              end_time: getTimeEnd(start_time, unit),
              number_of_people: applyDropdownSelection,
            })
          )

          const reservation_frames = unCheckedExistingFrames
            .concat(checkedFrames)
            .filter((frame) => frame.number_of_people > 0) // NOTE: number_of_people === 0 は弾く

          return { day_of_week, reservation_frames }
        })
        .filter(({ reservation_frames }) => reservation_frames.length > 0)

    // 変更がある場合のみonChangeを呼ぶ
    if (
      !isEqualRegularReservationSettings(
        appliedReservationSettings,
        reservationSettingsRef.current
      )
    ) {
      onChangeRef.current?.(appliedReservationSettings)
    }
  }, [checkStatuses, applyDropdownSelection, unit])

  const scrollRef = useRef<ElementRef<'div'> | null>()
  const [headerPaddingRight, setHeaderPaddingRight] = useState(0)
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = INITIAL_SCROLL_TOP
      setHeaderPaddingRight(
        scrollRef.current.offsetWidth - scrollRef.current.clientWidth
      )
    }
  }, [startEnds])

  const onChangeSetting = useCallback(
    (setting: RegularReservationSettingProps) => {
      const settings = reservationSettingsRef.current
      const settingsWithoutTarget = settings.filter(
        ({ day_of_week }) => day_of_week !== setting.day_of_week
      )
      if (setting.reservation_frames.length > 0) {
        settingsWithoutTarget.push(setting)
      }
      onChangeRef.current?.(settingsWithoutTarget)
    },
    []
  )

  useEffect(() => {
    const fromNumber = timeStringToNumber(from)
    const toNumber = timeStringToNumber(to)
    if (fromNumber >= toNumber) return //NOTE: 異常な値の時は値を変更しない
    const settings = reservationSettingsRef.current
      ?.map(({ reservation_frames, ...props }) => {
        return {
          ...props,
          reservation_frames: reservation_frames.filter(
            ({ start_time, end_time }) => {
              const startNumber = timeStringToNumber(start_time)
              const endNumber = timeStringToNumber(end_time)
              return fromNumber <= startNumber && endNumber <= toNumber
            }
          ),
        }
      })
      .filter(({ reservation_frames }) => reservation_frames.length > 0)
    onChangeRef.current?.(settings)
  }, [from, to, unit])

  const onChangeCheckStatus = useCallback(
    (checkStatus: DayOfWeekCheckStatus, dayOfWeek: number) => {
      setCheckStatuses((prevState) => ({
        ...prevState,
        [dayOfWeek]: checkStatus,
      }))
    },
    []
  )

  return startEnds.length ? (
    <div className={classes.root}>
      {!readOnly && (
        <Box
          display="flex"
          justifyContent="space-between"
          padding="16px"
          alignItems="center"
        >
          <Box flexGrow="1">
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                setCheckStatuses(
                  Object.fromEntries(dayOfWeeks.map((d) => [d, {}]))
                )
              }}
            >
              {t('全ての選択を外す')}
            </Button>
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Select
                  displayEmpty
                  variant="outlined"
                  defaultValue={0}
                  value={applyDropdownSelection}
                  className={classes.applyDropdown}
                >
                  {/* NOTE: 同じ項目が選択された場合にonChangeで選択が取れないため、MenuItemのonClickを利用する */}
                  <MenuItem
                    value={0}
                    onClick={() => setApplyDropdownSelection(0)}
                  >
                    {t('なし')}
                  </MenuItem>
                  {[...new Array(maxNumber)].map((_, i) => {
                    const value = i + 1
                    return (
                      <MenuItem
                        key={i}
                        value={value}
                        onClick={() => setApplyDropdownSelection(value)}
                      >
                        {i + 1}
                        {t('人', { count: i + 1 })}
                      </MenuItem>
                    )
                  })}
                </Select>
              }
              label={t('選択項目に適用')}
              labelPlacement="start"
              className={classes.applyDropdownContainer}
            />
          </Box>
        </Box>
      )}
      <div className={classes.tableContainer}>
        <div className={cx(!readOnly && classes.tableScrollXContainer)}>
          <div
            className={classes.dayOfWeeks}
            style={{ paddingRight: headerPaddingRight }}
          >
            <ReservationFrameColumn>
              <ReservationFrameCell />
            </ReservationFrameColumn>
            {dayOfWeeks.map((dayOfWeek) => (
              <RegularReservationFrameDayOfWeek
                key={dayOfWeek}
                dayOfWeek={dayOfWeek}
                checkStatusesOfDay={checkStatuses[dayOfWeek]}
                setDayOfWeekChecked={(checked) => {
                  setCheckStatuses((prevState) => ({
                    ...prevState,
                    [dayOfWeek]: Object.fromEntries(
                      startEnds.map(({ start }) => [start, checked])
                    ),
                  }))
                }}
                startEnds={startEnds}
                readOnly={readOnly}
              />
            ))}
          </div>
          <div
            className={classes.scrollContainer}
            ref={(ref) => (scrollRef.current = ref)}
          >
            <ReservationFrameTimes values={startEnds} />
            {dayOfWeeks.map((dayOfWeek) => (
              <RegularReservationSettingColumn
                unit={unit}
                key={dayOfWeek}
                dayOfWeek={dayOfWeek}
                reservationSetting={reservationSettingsRef.current.find(
                  (setting) => setting.day_of_week === dayOfWeek
                )}
                checkStatus={checkStatuses[dayOfWeek]}
                onChange={onChangeSetting}
                onChangeCheckStatus={onChangeCheckStatus}
                startEnds={startEnds}
                readOnly={readOnly}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  ) : null
}

export default RegularReservationFrameSetting
